import React, { useState, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { useAuth } from '../../stateManagers/services/AuthContext';

const StudentLoginScreen = () => {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const toastRef = useRef<Toast>(null);

  const handleLogin = async () => {
    if (!email || !password) {
      toastRef.current?.show({
        severity: 'warn',
        summary: 'Missing Fields',
        detail: 'Enter your roll number / email and password.',
        life: 3000,
      });
      return;
    }

    setIsLoggingIn(true);

    try {
      await login(email, password);
      // Success - AuthContext will update isAuthenticated and App will handle navigation
    } catch (err: any) {
      console.error('Login failed:', err);

      const errMsg =
        err?.response?.data?.message ||
        err?.message ||
        'Login failed. Please check your credentials.';

      toastRef.current?.show({
        severity: 'error',
        summary: 'Login Failed',
        detail: errMsg,
        life: 5000,
      });
    } finally {
      setIsLoggingIn(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#F5F7FA',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <Toast ref={toastRef} position="top-right" />

      <div className="hci-login-card" style={{
        display: 'flex',
        width: '100%',
        maxWidth: '80rem',
        minHeight: '600px',
        background: 'white',
        borderRadius: '24px',
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(14, 34, 93, 0.15)',
        border: '1px solid #E5E7EB'
      }}>
        {/* Left Panel - Student timetable strip motif */}
        <div className="hci-login-aside" style={{
          flex: 1,
          background: 'linear-gradient(135deg, #0e225d 0%, #1a3370 50%, #fbb21f 100%)',
          backgroundSize: '180% 180%',
          padding: '60px 50px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          position: 'relative',
          overflow: 'hidden',
          animation: 'hci-gradient-drift 14s ease-in-out infinite'
        }}>
          {/* Decorative blur circles */}
          <div style={{
            position: 'absolute',
            top: '-100px',
            right: '-100px',
            width: '300px',
            height: '300px',
            background: 'rgba(251, 178, 31, 0.2)',
            borderRadius: '50%',
            filter: 'blur(60px)'
          }} />
          <div style={{
            position: 'absolute',
            bottom: '-150px',
            left: '-100px',
            width: '400px',
            height: '400px',
            background: 'rgba(14, 34, 93, 0.3)',
            borderRadius: '50%',
            filter: 'blur(80px)'
          }} />

          {/* Logo */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            position: 'relative',
            zIndex: 1
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              background: '#fbb21f',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid rgba(255, 255, 255, 0.2)'
            }}>
              <i className="pi pi-th-large" style={{ fontSize: '1.4rem', color: '#0e225d' }}></i>
            </div>
            <span style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: 'white',
              letterSpacing: '-0.5px'
            }}>
              HCI Student Portal
            </span>
          </div>

          {/* Main Message */}
          <div style={{ position: 'relative', zIndex: 1 }}>
            <p style={{
              fontSize: '1rem',
              color: 'rgba(255, 255, 255, 0.8)',
              marginBottom: '16px',
              fontWeight: '400'
            }}>
              One login for
            </p>
            <h1 style={{
              fontSize: '2.5rem',
              color: 'white',
              margin: 0,
              fontWeight: '600',
              lineHeight: '1.2',
              letterSpacing: '-1px'
            }}>
              Stay on top of Institute, every single day.
            </h1>
          </div>

          <div style={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            {[0, 1, 2, 3, 4, 5, 6].map((day) => (
              <div
                key={day}
                className="hci-day-dot"
                style={{
                  width: day === 3 ? '14px' : '8px',
                  height: day === 3 ? '14px' : '8px',
                  borderRadius: '50%',
                  background: day === 3 ? '#fbb21f' : 'rgba(255, 255, 255, 0.35)',
                  animationDelay: `${day * 0.18}s`
                }}
              />
            ))}
            <span style={{
              marginLeft: '8px',
              fontSize: '0.8rem',
              color: 'rgba(255, 255, 255, 0.7)',
              fontWeight: '500'
            }}>
              Today, and every day before it
            </span>
          </div>
        </div>

        {/* Right Panel - Login Form */}
        <div style={{
          flex: 1,
          padding: '60px 50px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          background: 'white'
        }}>
          {/* Icon */}
          <div style={{
            width: '48px',
            height: '48px',
            background: '#E8EEF7',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '24px',
            border: '2px solid #fbb21f'
          }}>
            <i className="pi pi-graduation-cap" style={{ fontSize: '1.3rem', color: '#0e225d' }}></i>
          </div>

          {/* Title */}
          <h2 style={{
            fontSize: '2rem',
            fontWeight: '700',
            color: '#0e225d',
            margin: '0 0 8px 0',
            letterSpacing: '-0.5px'
          }}>
            Hey there, student!
          </h2>
          <p style={{
            fontSize: '0.95rem',
            color: '#6b7280',
            margin: '0 0 32px 0'
          }}>
            Sign in to check your attendance, marks, and class updates.
          </p>

          {/* Email Input */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '0.9rem',
              fontWeight: '600',
              color: '#0e225d',
              marginBottom: '8px'
            }}>
              Roll number or email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="yourname@hcistudent.in"
              autoComplete="email"
              disabled={isLoggingIn}
              style={{
                width: '100%',
                padding: '14px 16px',
                fontSize: '1rem',
                border: '2px solid #E5E7EB',
                borderRadius: '12px',
                outline: 'none',
                transition: 'all 0.2s ease',
                boxSizing: 'border-box',
                fontFamily: 'inherit',
                backgroundColor: 'white',
                opacity: isLoggingIn ? 0.6 : 1
              }}
              onFocus={(e) => {
                if (!isLoggingIn) {
                  e.target.style.borderColor = '#0e225d';
                  e.target.style.boxShadow = '0 0 0 3px rgba(14, 34, 93, 0.1)';
                }
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#E5E7EB';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          {/* Password Input */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{
              display: 'block',
              fontSize: '0.9rem',
              fontWeight: '600',
              color: '#0e225d',
              marginBottom: '8px'
            }}>
              Password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••••"
                autoComplete="current-password"
                disabled={isLoggingIn}
                style={{
                  width: '100%',
                  padding: '14px 50px 14px 16px',
                  fontSize: '1rem',
                  border: '2px solid #E5E7EB',
                  borderRadius: '12px',
                  outline: 'none',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box',
                  fontFamily: 'inherit',
                  backgroundColor: 'white',
                  opacity: isLoggingIn ? 0.6 : 1
                }}
                onFocus={(e) => {
                  if (!isLoggingIn) {
                    e.target.style.borderColor = '#0e225d';
                    e.target.style.boxShadow = '0 0 0 3px rgba(14, 34, 93, 0.1)';
                  }
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#E5E7EB';
                  e.target.style.boxShadow = 'none';
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isLoggingIn && email && password) {
                    handleLogin();
                  }
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isLoggingIn}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                style={{
                  position: 'absolute',
                  right: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: isLoggingIn ? 'not-allowed' : 'pointer',
                  color: '#6b7280',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: isLoggingIn ? 0.5 : 1
                }}
              >
                <i className={`pi ${showPassword ? 'pi-eye-slash' : 'pi-eye'}`} style={{ fontSize: '1.1rem' }}></i>
              </button>
            </div>
          </div>

          {/* Forgot password link */}
          <div style={{ textAlign: 'right', marginBottom: '24px' }}>
            <a href="#" style={{
              fontSize: '0.85rem',
              color: '#0e225d',
              textDecoration: 'none',
              fontWeight: '600'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#fbb21f'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#0e225d'}
            >
              Forgot password?
            </a>
          </div>

          {/* Login Button */}
          <button
            onClick={handleLogin}
            disabled={isLoggingIn || !email || !password}
            style={{
              width: '100%',
              padding: '16px',
              background: (isLoggingIn || !email || !password) ? '#9ca3af' : '#0e225d',
              color: 'white',
              border: (isLoggingIn || !email || !password) ? '2px solid #E5E7EB' : '2px solid #fbb21f',
              borderRadius: '12px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: (isLoggingIn || !email || !password) ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              marginBottom: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => {
              if (!isLoggingIn && email && password) {
                e.currentTarget.style.background = '#1a3370';
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(14, 34, 93, 0.3)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoggingIn && email && password) {
                e.currentTarget.style.background = '#0e225d';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }
            }}
          >
            {isLoggingIn ? (
              <>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  borderTop: '2px solid white',
                  borderRadius: '50%',
                  animation: 'hci-spin 0.8s linear infinite'
                }} />
                Signing in...
              </>
            ) : (
              <>
                Go to my dashboard
                <i className="pi pi-arrow-right" style={{ fontSize: '0.9rem' }}></i>
              </>
            )}
          </button>

          {/* Footer Link */}
          <p style={{
            textAlign: 'center',
            fontSize: '0.9rem',
            color: '#6b7280',
            margin: 0
          }}>
            Lost your login details?{' '}
            <a href="#" style={{
              color: '#0e225d',
              textDecoration: 'none',
              fontWeight: '600',
              transition: 'color 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#fbb21f'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#0e225d'}
            >
              Ask your class teacher
            </a>
          </p>
        </div>
      </div>

      {/* Responsive + animation styles */}
      <style>{`
        @keyframes hci-spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes hci-gradient-drift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes hci-day-pulse {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.15); }
        }
        .hci-day-dot {
          animation: hci-day-pulse 2.4s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .hci-login-aside {
            animation: none !important;
          }
          .hci-day-dot {
            animation: none !important;
          }
        }
        @media (max-width: 768px) {
          .hci-login-aside {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

export default StudentLoginScreen;